import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";

const LOGO_URL = "https://customer-assets-v7afamib.emergentagent.net/job_ebenezer-ghana-sda/artifacts/861qgsvb_garland-ebenezer-ghanaian-seventh-day-adventist-church-logo%20%281%29.webp";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/visit", label: "Visit" },
  { to: "/sermons", label: "Sermons" },
  { to: "/explore", label: "Explore" },
  { to: "/events", label: "Events" },
  { to: "/ministries", label: "Ministries" },
  { to: "/gallery", label: "Gallery" },
  { to: "/give", label: "Give" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Only the home page has a hero (transparent header at top).
  const isHome = location.pathname === "/";
  // Header is "on light" only when not home and not scrolled-over-navy; otherwise dark surface.
  const overHero = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Header styling logic:
  // - overHero (home, not scrolled): transparent, white text
  // - scrolled OR non-home page: navy background, white text
  const headerBg = overHero
    ? "bg-transparent"
    : "bg-[#003B5C] shadow-md";

  const linkInactive = "text-white/80 hover:text-white";
  const linkActive = "text-white";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 xl:px-8">
        <div className="flex items-center justify-between gap-3 h-[76px] sm:h-[84px] lg:h-[92px]">
          {/* Logo — official Adventist lockup (symbol + Seventh-day Adventist Church + church name subline) */}
          <Link
            to="/"
            data-testid="navbar-logo"
            aria-label="Garland Ebenezer Ghanaian Seventh-day Adventist Church — Home"
            className="flex items-center group min-w-0 lg:min-w-fit flex-1 lg:flex-none"
          >
            <img
              src={LOGO_URL}
              alt="Garland Ebenezer Ghanaian Seventh-day Adventist Church"
              data-testid="navbar-logo-image"
              className="h-12 sm:h-14 lg:h-16 xl:h-[72px] w-auto object-contain shrink-0"
              style={{ filter: "brightness(0) invert(1)" }}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0 xl:gap-1 shrink-0">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-2 xl:px-3.5 py-2 text-[12px] xl:text-sm font-medium transition-colors relative whitespace-nowrap ${
                    isActive ? linkActive : linkInactive
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-[2px] bg-[#D4A437]" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link
              to="/sermons"
              data-testid="navbar-search"
              aria-label="Search sermons"
              className="w-10 h-10 hidden sm:flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 rounded-sm transition-colors"
            >
              <Search size={18} />
            </Link>
            <Link
              to="/visit"
              data-testid="navbar-cta-visit"
              className="hidden 2xl:inline-flex items-center gap-2 bg-[#D4A437] text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#B88C2A] transition-all whitespace-nowrap"
            >
              Plan Your Visit
            </Link>
            <button
              data-testid="navbar-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-white focus:outline-none focus:ring-2 focus:ring-[#D4A437]/60 focus:ring-offset-2 focus:ring-offset-[#003B5C] rounded-sm"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div
            id="mobile-menu"
            data-testid="navbar-mobile-menu"
            className="lg:hidden pb-6 pt-2 space-y-1 border-t border-white/15 bg-[#003B5C] -mx-4 sm:-mx-6 px-4 sm:px-6"
          >
            <div className="flex items-center gap-3 px-2 pt-4 pb-5 border-b border-white/10 mb-2">
              <img
                src={LOGO_URL}
                alt="Garland Ebenezer Ghanaian Seventh-day Adventist Church"
                data-testid="mobile-menu-logo"
                className="h-14 w-auto object-contain shrink-0"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </div>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-4 py-3 text-base font-medium rounded-sm ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/80 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/visit"
              data-testid="navbar-mobile-cta-visit"
              className="block mx-4 mt-3 text-center bg-[#D4A437] text-white px-5 py-3 rounded-sm text-sm font-medium"
            >
              Plan Your Visit
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};
